<template>
  <SharedEditDialog>
    <template #title>
      <p class="title">{{ messages.editAllowedStudent }}</p>
    </template>
    <template #content>
      <div class="mt-6 input-container">
        <BaseCheckboxReg v-model="acceptingNewStudents" :label="messages.acceptingNewStudents" />
      </div>
      <div v-if="!acceptingNewStudents" class="mt-6 input-container">
        <v-data-table v-model="studentList" v-bind="tableConfig" />
      </div>
    </template>
    <template #actions="{ closeDialog }">
      <BaseButton
        outlined
        :clear="true"
        color="grayscale-darken3"
        class="mr-4 cancel-button"
        @click="closeDialog"
      >
        {{ messages.void }}
      </BaseButton>
      <BaseButton class="confirm-button" @click="handleSubmit(closeDialog)">{{
        messages.confirmChanges
      }}</BaseButton>
    </template>
  </SharedEditDialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import validateInputDefer from "@/mixins/validateInputDefer"
import notyMixin from "@/mixins/notyMixin"
import spinnerMixin from "@/mixins/spinnerMixin"
import { sync } from "vuex-pathify"

export default {
  mixins: [validateInputDefer, notyMixin, spinnerMixin],

  data: () => ({
    acceptingNewStudents: true,
    studentList: [],
    selected: [],
  }),

  computed: {
    ...mapGetters("layout", {
      dialogData: "getDialogConfig",
    }),
    allowedStudents: sync("profile/allowedStudents"),
    myStudents: sync("profile/myStudents"),
    messages() {
      return {
        editAllowedStudent: this.$t("message.profile.editAllowedStudent"),
        acceptingNewStudents: this.$t("message.profile.acceptingNewStudents"),
        void: this.$t("message.void"),
        confirmChanges: this.$t("message.confirmChanges"),
      }
    },
    tableConfig() {
      return {
        headers: [
          {
            text: this.$t("message.profile.selectAll"),
            align: "start",
            sortable: false,
            value: "student",
          },
        ],
        items: this.myStudents.map((student) => ({ student })),
        itemKey: "student",
        showSelect: true,
        disablePagination: true,
        hideDefaultFooter: true,
      }
    },
  },
  methods: {
    ...mapActions("profile", {
      updateAllowedStudents: "updateAllowedStudents",
      getMyStudents: "setMyStudents",
      updateProfile: "updateLanguages", //updates profile without removing falsy
    }),

    async handleSubmit(close) {
      if (!this.acceptingNewStudents && this.studentList.length === 0) {
        this.simpleError({ text: this.$t("message.profile.selectAtLeastOneStudent") })
        return
      }
      try {
        this.isLoading()
        if (this.acceptingNewStudents) {
          await this.updateProfile({ not_accepting_new_students: false })
        } else {
          await this.updateAllowedStudents(
            this.studentList.map((item) => item.student).filter(Boolean),
          )
          await this.updateProfile({ not_accepting_new_students: true })
        }
        this.simpleSuccess({ text: this.$t("message.success.editStudents") })
      } catch (_) {
        this.simpleError({ text: this.$t("message.error.updateData") })
        return Promise.reject("failed to update password")
      } finally {
        this.isLoaded()
        close()
      }
    },
  },
  created() {
    this.getMyStudents()
    const { acceptingNewStudents, approvedStudents } = this.dialogData.data
    this.studentList = approvedStudents.map((item) => ({ student: item.student.email }))
    this.acceptingNewStudents = acceptingNewStudents
  },
}
</script>

<style lang="scss" scoped>
@import "@/sass/rwd_breakpoints.scss";

.title {
  font-weight: 600;
  font-size: 1.25rem;
  color: var(--v-grayscale-darken3);
}

.cancel-button {
  width: 81px !important;
  height: 49px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: capitalize;
  border-radius: 8px;
  padding: 0;
  border: 1px solid var(--v-grayscale-lighten4);
  color: var(--v-grayscale-darken3) !important;
}

.input-container {
  position: relative;

  &__title {
    font-weight: 500;
    font-size: 16px;
    color: var(--v-primary-darken3);
  }

  &__text-area {
    width: 100%;
    height: 400px;
    background-color: var(--v-primary-lighten2) !important;
    border: 1px solid var(--v-grayscale-lighten4);
    border-radius: 0.5rem;
    resize: none;
    padding: 0.8rem;

    &:focus {
      outline: none;
    }

    &--error {
      color: var(--v-error-darken1);
      border: 2px solid var(--v-error-darken1);
    }
  }

  &__error-message {
    position: absolute;
    bottom: -25px;
    font-size: 0.875rem;
    color: var(--v-error-darken1);
    @media (max-width: $bp-m) {
      bottom: -40px;
    }
  }
}
</style>
